import React, { useState, useEffect } from 'react';
import { Card, Form, Select, DatePicker, Button, Row, Col, List, Image, Tag, Modal, Descriptions, Switch, message } from 'antd';
import { CarOutlined, EnvironmentOutlined, UserOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { RootState } from '../../store/store';
import { setFilter, fetchCarsSuccess, selectCar } from '../../store/slices/rentalSlice';
import { createOrderSuccess } from '../../store/slices/orderSlice';
import api from '../../utils/api';
import DemoBanner from '../../components/DemoBanner';

const { Option } = Select;
const { RangePicker } = DatePicker;

const RentalPage: React.FC = () => {
  const [form] = Form.useForm();
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cars, selectedCar } = useSelector((state: RootState) => state.rental);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  // サンプルデータ
  const mockCars = [
    {
      id: '1',
      brand: 'フォルクスワーゲン',
      model: 'Polo',
      year: 2023,
      plateNumber: '品川 330 あ 1234',
      price: 8800,
      deposit: 50000,
      available: true,
      images: ['https://via.placeholder.com/300x200?text=Polo'],
      features: ['オートマチック', '5人乗り', 'エコノミー', 'GPSナビ']
    },
    {
      id: '2',
      brand: 'ホンダ',
      model: 'シビック',
      year: 2023,
      plateNumber: '品川 330 い 5678',
      price: 11800,
      deposit: 75000,
      available: true,
      images: ['https://via.placeholder.com/300x200?text=Civic'],
      features: ['オートマチック', '5人乗り', 'コンパクト', 'バックモニター', 'サンルーフ']
    },
    {
      id: '3',
      brand: 'トヨタ',
      model: 'カムリ',
      year: 2023,
      plateNumber: '品川 330 う 9012',
      price: 16800,
      deposit: 100000,
      available: true,
      images: ['https://via.placeholder.com/300x200?text=Camry'],
      features: ['オートマチック', '5人乗り', 'ビジネス', 'レザーシート', 'オートエアコン']
    },
    {
      id: '4',
      brand: 'メルセデス・ベンツ',
      model: 'C200',
      year: 2023,
      plateNumber: '品川 330 え 3456',
      price: 28800,
      deposit: 150000,
      available: true,
      images: ['https://via.placeholder.com/300x200?text=Mercedes'],
      features: ['オートマチック', '5人乗り', '高級車', 'レザーシート', '運転支援システム']
    }
  ];

  const cities = ['東京', '大阪', '名古屋', '福岡', '札幌'];
  const stores = ['新宿店', '渋谷店', '羽田空港店', '東京駅店'];

  useEffect(() => {
    // 初期読み込み時は検索せずに空の状態にする
    // ユーザーが検索条件を入力して検索ボタンを押すまで待つ
    dispatch(fetchCarsSuccess([]));
  }, [dispatch]);

  const handleSearch = async (values: any) => {
    try {
      setLoading(true);
      dispatch(setFilter(values));
      
      // Build search parameters
      const searchParams: any = {};
      
      if (values.city) searchParams.city = values.city;
      if (values.storeId) searchParams.storeId = values.storeId;
      if (values.dateRange && values.dateRange.length === 2) {
        searchParams.startDate = values.dateRange[0].toISOString();
        searchParams.endDate = values.dateRange[1].toISOString();
      }
      if (values.category) searchParams.category = values.category;
      if (values.minPrice) searchParams.minPrice = values.minPrice;
      if (values.maxPrice) searchParams.maxPrice = values.maxPrice;
      if (values.fuelType) searchParams.fuelType = values.fuelType;
      if (values.transmission) searchParams.transmission = values.transmission;
      if (values.seats) searchParams.seats = values.seats;
      if (values.features) searchParams.features = values.features;
      
      const response = await api.get('/api/vehicles/search', { params: searchParams });
      
      if (response.data.success) {
        const vehicleData = response.data.data.vehicles.map((vehicle: any) => ({
          id: vehicle._id,
          brand: vehicle.brand,
          model: vehicle.model,
          year: vehicle.year,
          plateNumber: vehicle.plateNumber,
          price: vehicle.dailyRate,
          deposit: vehicle.depositAmount,
          available: true, // API already filters available vehicles
          images: vehicle.images || ['https://via.placeholder.com/300x200?text=' + vehicle.brand],
          features: vehicle.features || [],
          category: vehicle.category,
          fuelType: vehicle.fuelType,
          transmission: vehicle.transmission,
          seats: vehicle.seats,
          store: vehicle.store?.[0] || null,
          company: vehicle.company?.[0] || null
        }));
        
        dispatch(fetchCarsSuccess(vehicleData));
        message.success(`検索が完了しました。${vehicleData.length}台の車両が見つかりました。`);
      } else {
        message.error(response.data.message || '検索中にエラーが発生しました');
      }
    } catch (error: any) {
      console.error('Vehicle search error:', error);
      message.error(error.response?.data?.message || '検索中にエラーが発生しました');
      // Fallback to mock data on error
      dispatch(fetchCarsSuccess(mockCars));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCar = async (car: any) => {
    // 检查用户是否已登录
    if (!isAuthenticated) {
      Modal.confirm({
        title: 'ログインが必要です',
        content: '車両をご予約いただくには、ログインまたは新規登録が必要です。',
        okText: 'ログインする',
        cancelText: '新規登録',
        onOk() {
          navigate('/login');
        },
        onCancel() {
          navigate('/register');
        },
      });
      return;
    }

    // Check if date range is selected for availability check
    const dateRange = form.getFieldValue('dateRange');
    if (dateRange && dateRange.length === 2) {
      try {
        message.loading('車両の空車状況を確認中...', 1);
        
        const response = await api.get(`/api/vehicles/${car.id}/availability`, {
          params: {
            startDate: dateRange[0].toISOString(),
            endDate: dateRange[1].toISOString()
          }
        });
        
        if (response.data.success && response.data.data.available) {
          // Add pricing information to car object
          const carWithPricing = {
            ...car,
            pricing: response.data.data.pricing
          };
          dispatch(selectCar(carWithPricing));
          setOrderModalVisible(true);
        } else {
          const conflictingOrders = response.data.data.conflictingOrders || [];
          let conflictMessage = '申し訳ございませんが、この車両は選択された期間中に利用できません。';
          
          if (conflictingOrders.length > 0) {
            conflictMessage += `\n\n既存の予約:`;
            conflictingOrders.forEach((order: any) => {
              conflictMessage += `\n• ${order.orderNumber}: ${dayjs(order.startDateTime).format('YYYY/MM/DD HH:mm')} - ${dayjs(order.endDateTime).format('YYYY/MM/DD HH:mm')}`;
            });
          }
          
          Modal.warning({
            title: '車両が利用できません',
            content: conflictMessage,
            okText: '他の車両を選択'
          });
        }
      } catch (error: any) {
        console.error('Availability check error:', error);
        message.error('空車確認中にエラーが発生しました');
        // Fallback to simple selection without availability check
        dispatch(selectCar(car));
        setOrderModalVisible(true);
      }
    } else {
      // If no date range selected, proceed with simple selection
      Modal.info({
        title: 'レンタル期間を選択してください',
        content: '車両の空車状況を確認するため、まず検索フォームでレンタル期間を選択してください。',
        okText: '了解'
      });
    }
  };

  const handleConfirmOrder = (orderData: any) => {
    if (!selectedCar) return;

    // Use pricing from API if available, otherwise fallback to basic calculation
    let totalAmount = selectedCar.price;
    let deposit = selectedCar.deposit;
    
    if (selectedCar.pricing) {
      totalAmount = selectedCar.pricing.basePrice;
      deposit = selectedCar.pricing.deposit;
    } else {
      // Fallback calculation
      const days = dayjs(orderData.returnDate).diff(dayjs(orderData.pickupDate), 'day');
      totalAmount = selectedCar.price * Math.max(days, 1);
    }

    const order = {
      id: Date.now().toString(),
      carId: selectedCar.id,
      carInfo: {
        brand: selectedCar.brand,
        model: selectedCar.model,
        plateNumber: selectedCar.plateNumber,
      },
      pickupDate: orderData.pickupDate,
      returnDate: orderData.returnDate,
      pickupLocation: orderData.pickupLocation,
      returnLocation: orderData.returnLocation,
      totalAmount,
      deposit,
      duration: selectedCar.pricing?.duration || {
        days: Math.max(dayjs(orderData.returnDate).diff(dayjs(orderData.pickupDate), 'day'), 1),
        hours: 0
      },
      status: 'pending' as const,
      paymentStatus: 'unpaid' as const,
      createdAt: new Date().toISOString(),
    };

    dispatch(createOrderSuccess(order));
    setOrderModalVisible(false);
    message.success('ご注文が正常に作成されました！');
    navigate('/orders');
  };

  return (
    <div style={{ padding: '20px 0' }}>
      {/* Demo標識 */}
      <DemoBanner />

      <div className="container">
        {/* 検索フォーム */}
        <Card className="search-form" style={{ marginBottom: 20 }}>
          <Form
            form={form}
            onFinish={handleSearch}
            layout="vertical"
          >
            <Row gutter={16}>
              <Col xs={24} sm={6}>
                <Form.Item name="city" label="都市" initialValue="東京">
                  <Select placeholder="都市を選択">
                    {cities.map(city => (
                      <Option key={city} value={city}>{city}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item name="storeId" label="店舗">
                  <Select placeholder="店舗を選択">
                    {stores.map(store => (
                      <Option key={store} value={store}>{store}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item 
                  name="dateRange" 
                  label="レンタル期間"
                  rules={[
                    { required: true, message: 'レンタル期間を選択してください' }
                  ]}
                >
                  <RangePicker
                    showTime
                    placeholder={['出発日時', '返却日時']}
                    style={{ width: '100%' }}
                    disabledDate={(current) => {
                      // 過去の日付を無効にする
                      return current && current < dayjs().startOf('day');
                    }}
                    showNow={false}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={4}>
                <Form.Item label=" ">
                  <Button type="primary" htmlType="submit" block>
                    車両検索
                  </Button>
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item name="returnToDifferentLocation" valuePropName="checked">
                  <Switch /> 乗り捨て返却
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* 車両リスト */}
        {cars.length === 0 && !loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <CarOutlined style={{ fontSize: '48px', color: '#bfbfbf', marginBottom: '16px' }} />
            <h3 style={{ color: '#bfbfbf' }}>車両が見つかりませんでした</h3>
            <p style={{ color: '#999' }}>
              検索条件を変更して再度お試しください。<br/>
              レンタル期間と店舗を選択して検索してください。
            </p>
          </div>
        ) : (
          <List
            grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}
            dataSource={cars}
            loading={loading}
            renderItem={(car) => (
            <List.Item>
              <Card
                hoverable
                className="car-card"
                cover={
                  <Image
                    alt={`${car.brand} ${car.model}`}
                    src={car.images[0]}
                    height={200}
                    style={{ objectFit: 'cover' }}
                  />
                }
                actions={[
                  <Button 
                    type="primary" 
                    disabled={!car.available}
                    onClick={() => handleSelectCar(car)}
                  >
                    {car.available ? '今すぐ予約' : '利用不可'}
                  </Button>
                ]}
              >
                <Card.Meta
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{car.brand} {car.model} {car.year}</span>
                      <Tag color={car.available ? 'green' : 'red'}>
                        {car.available ? 'レンタル可能' : 'レンタル中'}
                      </Tag>
                    </div>
                  }
                  description={
                    <div>
                      <div style={{ margin: '8px 0' }}>
                        <CarOutlined /> ナンバープレート：{car.plateNumber}
                      </div>
                      <div style={{ margin: '8px 0' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {car.features.map(feature => (
                            <Tag key={feature}>{feature}</Tag>
                          ))}
                        </div>
                      </div>
                      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff' }}>
                          ¥{car.price.toLocaleString()}/日
                        </span>
                        <span style={{ fontSize: 12, color: '#666' }}>
                          保証金：¥{car.deposit.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  }
                />
              </Card>
            </List.Item>
          )}
        />
        )}

        {/* 注文確認モーダル */}
        <Modal
          title="ご注文の確認"
          open={orderModalVisible}
          onCancel={() => setOrderModalVisible(false)}
          width={600}
          footer={null}
        >
          {selectedCar && (
            <Form
              onFinish={handleConfirmOrder}
              layout="vertical"
            >
              <Descriptions title="車両情報" bordered size="small" style={{ marginBottom: 20 }}>
                <Descriptions.Item label="車種">{selectedCar.brand} {selectedCar.model}</Descriptions.Item>
                <Descriptions.Item label="年式">{selectedCar.year}</Descriptions.Item>
                <Descriptions.Item label="ナンバープレート">{selectedCar.plateNumber}</Descriptions.Item>
                <Descriptions.Item label="1日料金">¥{selectedCar.price.toLocaleString()}</Descriptions.Item>
                <Descriptions.Item label="保証金">¥{selectedCar.deposit.toLocaleString()}</Descriptions.Item>
                {selectedCar.pricing && (
                  <>
                    <Descriptions.Item label="期間">
                      {selectedCar.pricing.duration.days}日 {selectedCar.pricing.duration.hours}時間
                    </Descriptions.Item>
                    <Descriptions.Item label="基本料金">¥{selectedCar.pricing.basePrice.toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="合計金額" span={2}>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                        ¥{selectedCar.pricing.total.toLocaleString()}
                      </span>
                      <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                        (基本料金: ¥{selectedCar.pricing.basePrice.toLocaleString()} + 保証金: ¥{selectedCar.pricing.deposit.toLocaleString()})
                      </span>
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>

              <Form.Item
                name="pickupDate"
                label="出発日時"
                rules={[{ required: true, message: '出発日時を選択してください' }]}
              >
                <DatePicker showTime placeholder="出発日時を選択" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="returnDate"
                label="返却日時"
                rules={[{ required: true, message: '返却日時を選択してください' }]}
              >
                <DatePicker showTime placeholder="返却日時を選択" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="pickupLocation"
                label="出発場所"
                rules={[{ required: true, message: '出発場所を選択してください' }]}
              >
                <Select placeholder="出発場所を選択">
                  {stores.map(store => (
                    <Option key={store} value={store}>{store}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="returnLocation"
                label="返却場所"
                rules={[{ required: true, message: '返却場所を選択してください' }]}
              >
                <Select placeholder="返却場所を選択">
                  {stores.map(store => (
                    <Option key={store} value={store}>{store}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="deliveryType" label="受け取り方法" initialValue="store">
                <Select>
                  <Option value="store">店舗受け取り</Option>
                  <Option value="delivery">配送サービス（追加料金）</Option>
                </Select>
              </Form.Item>

              <div style={{ textAlign: 'right', marginTop: 20 }}>
                <Button style={{ marginRight: 8 }} onClick={() => setOrderModalVisible(false)}>
                  キャンセル
                </Button>
                <Button type="primary" htmlType="submit">
                  注文確定
                </Button>
              </div>
            </Form>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default RentalPage;